
import { defineComponent } from 'vue'
import { format, parseISO, differenceInDays, eachMonthOfInterval, subMonths, addMinutes } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import { PrimaryButton, Input, Select, Form, TextArea, Table } from '@design-system/ui'
import { faPaperclip, faPaperPlane, faSearch, faTimesCircle, faTrash, faFileAlt, faMailBulk, faCircleCheck, faMoneyBill } from '@fortawesome/free-solid-svg-icons'
import SessionService from '../SessionService'
import Toast from '@/components/Toast.vue'
import api from '@/services/api'

export default defineComponent({
  name: 'Aprovacoes',
  components: {
    Table,
    PrimaryButton,
    Input,
    TextArea,
    Form,
    Select,
    Toast
  },
  data () {
    return {
      errorMessage: null,
      months: [],
      selectedMonth: {
        value: '',
        key: ''
      },
      toastSuccess: true,
      message: '',
      hidden: true,
      timeSeen: 20000,
      loading: false,
      commissionRequests: [],
      statusAcceptedInUpdating: [
        {
          value: 'Aprovado',
          key: 'approved'
        },
        {
          value: 'Rejeitado',
          key: 'rejected'
        }
      ],
      buttonIcons: {
        search: {
          icon: faSearch
        },
        sendEmail: {
          icon: faPaperPlane
        },
        cancel: {
          icon: faTimesCircle
        },
        trash: {
          icon: faTrash
        },
        upload: {
          icon: faPaperclip
        },
        file: {
          icon: faFileAlt
        },
        message: {
          icon: faMailBulk
        },
        done: {
          icon: faCircleCheck
        },
        commission: {
          icon: faMoneyBill
        }
      },
      commissionLoading: false,
      SOFTWARE_HOUSE_CNPJ: null,
      downloadInProgress: false,
      ptBRDate: ptBR,
      softwareHouse: {},
      startFilter: '',
      searchSoftwareHouseText: '',
      endFilter: '',
      softwareHouseFilter: '',
      protocolFilter: '',
      approveOrRejectCommissionRequestModal: false,
      selectedCommissionRequestStatusToUpdate: {
        value: 'Selecione...',
        key: ''
      },
      commissionRequestStatus: {
        pending: 'Aguardando',
        rejected: 'Rejeitado',
        scheduled: 'Agendado',
        finished: 'Finalizado'
      },
      selectedCommissionRequestStatus: {
        value: 'Todos',
        key: ''
      },
      commissionRequestStatusAccepted: [
        {
          value: 'Aprovado',
          key: 'approved'
        },
        {
          value: 'Rejeitado',
          key: 'rejected'
        }
      ],
      commissionRequestSelected: {},
      commissionRequestForm: {
        receiptDate: '',
        message: ''
      },
      commissionRequestsLoading: false,
      commissionRequestStatusOptions: [
        {
          value: 'Todos',
          key: ''
        },
        {
          value: 'Aguardando',
          key: 'pending'
        },
        {
          value: 'Agendado',
          key: 'scheduled'
        },
        {
          value: 'Rejeitado',
          key: 'rejected'
        },
        {
          value: 'Finalizado',
          key: 'finished'
        }
      ]
    }
  },
  async mounted () {
    const session = new SessionService()
    const token = localStorage.getItem('token')

    const actualMonth = new Date().getMonth()
    this.currentMonth = actualMonth

    const monthsInterval = eachMonthOfInterval({
      start: subMonths(new Date(), 13),
      end: subMonths(new Date(), 1)
    })

    for (const month of monthsInterval) {
      this.months.push({
        key: month,
        value: format(month, 'MM/yyyy')
      })
    }

    this.selectedMonth = this.months[this.months.length - 1]

    const dateNow = new Date()
    this.startFilter = format(subMonths(dateNow, 1), 'yyyy-MM-dd')
    this.endFilter = format(dateNow, 'yyyy-MM-dd')

    this.SOFTWARE_HOUSE_CNPJ = session.cnpj()
    this.softwareHouse = await session.softwareHouseData(token, this.SOFTWARE_HOUSE_CNPJ)
    this.cnpjSoftwareHouse = this.SOFTWARE_HOUSE_CNPJ

    this.getCommissionRequests()
  },
  methods: {
    getCommissionRequests (value) {
      if (value) {
        this.updateSearchSoftwareHouseText(value.primaryText)
      }
      this.commissionRequestsLoading = true

      const rangeValidated = this.validateDateRange(this.startFilter, this.endFilter)
      if (!rangeValidated) {
        this.commissionRequestsLoading = false
        return
      }

      const queries = {
        dateStart: `${this.startFilter}T00:00:00.000Z`,
        dateFinish: `${this.endFilter}T23:59:59.999Z`,
        softwareHouseCnpj: this.softwareHouseFilter.replace(/\D/g, '')
      }

      api.get('/commission-request', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: queries
      })
        .then(response => {
          const commissionRequestsReturned = response.data

          this.commissionRequests = commissionRequestsReturned.reduce((commissionsRequests, commissionRequest) => {
            if (commissionRequest.status === 'approved') {
              const currentDate = new Date()
              const currentDateAdjusted = addMinutes(currentDate, currentDate.getTimezoneOffset())
              const receiptDate = new Date(commissionRequest.receiptDate)
              const receiptDateAdjusted = addMinutes(receiptDate, receiptDate.getTimezoneOffset())

              if (receiptDateAdjusted < currentDateAdjusted) {
                commissionRequest.status = 'finished'
              } else {
                commissionRequest.status = 'scheduled'
                commissionRequest.message = `Previsão de pagamento: ${format(receiptDateAdjusted, 'dd/MM/yyyy')}`
              }
            }

            const matchesStatus = this.selectedCommissionRequestStatus.key && this.selectedCommissionRequestStatus.key === commissionRequest.status
            const matchesProtocol = this.protocolFilter && commissionRequest.protocol === this.protocolFilter

            const noFilters = !this.selectedCommissionRequestStatus.key && !this.protocolFilter
            const bothFiltersMatch = matchesStatus && matchesProtocol
            const onlyStatusMatches = matchesStatus && !this.protocolFilter
            const onlyProtocolMatches = !this.selectedCommissionRequestStatus.key && matchesProtocol

            if (noFilters || bothFiltersMatch || onlyStatusMatches || onlyProtocolMatches) {
              commissionsRequests.push(commissionRequest)
            }
            return commissionsRequests
          }, [])

          setTimeout(() => {
            var evt = new CustomEvent('list')
            window.dispatchEvent(evt)
          }, 1)

          this.commissionRequestsLoading = false
        })
        .catch(() => {
          this.commissionRequestsLoading = false
        })
    },
    async updateCommissionRequest () {
      this.loading = true

      try {
        const token = localStorage.getItem('token')
        const commissionRequest = this.commissionRequestSelected
        const commissionRequestForm = this.commissionRequestForm

        await api.patch(`/commission-request/${commissionRequest.protocol}`, commissionRequestForm, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        this.loading = false
        this.approveOrRejectCommissionRequestModal = false
        this.hidden = false
        this.toastSuccess = true
        this.message = 'Solicitação de repasse de comissão atualizada com sucesso'
        setTimeout(() => {
          this.hidden = true
        }, this.timeSeen)
        this.getCommissionRequests()
      } catch (error) {
        this.hidden = false
        this.toastSuccess = false
        this.message = error.response && error.response.data && error.response.data.error_messages[0] ? error.response.data.error_messages[0] : 'Ocorreu um erro ao alterar a solicitação de repasse de comissões'
        setTimeout(() => {
          this.hidden = true
        }, this.timeSeen)
      }
    },
    handleEdit (protocol) {
      const commissionRequest = this.commissionRequests.find(commissionRequest => commissionRequest.protocol === protocol)
      this.$router.push(`/aprovacoes/${commissionRequest.softwareHouse.cnpj}/${protocol}`)
    },
    handleCommissionRequestStatus (selected) {
      this.selectedCommissionRequestStatusToUpdate = selected
      this.commissionRequestForm.status = selected.key
    },
    validateDateRange (startDateStr: string, endDateStr: string) {
      const intervalDays = differenceInDays(new Date(endDateStr), new Date(startDateStr))
      const maxInterval = 90

      if (new Date(endDateStr) > new Date()) {
        this.errorMessage = 'Não é possível buscar dados futuros'
        return false
      }

      if (intervalDays > maxInterval) {
        this.errorMessage = 'Intervalo de datas maior que 90 dias'
        return false
      }

      this.errorMessage = ''
      return true
    },
    handleCommissionProtocolStatus (selected) {
      this.selectedCommissionRequestStatus = selected
    },
    formatCnpj (cnpj: string): string {
      const cnpjFormatado = `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
      return cnpjFormatado
    },
    hide () {
      this.hidden = true
    },
    formatDate: format,
    parseISODate: parseISO
  },
  beforeMount () {
    this.$store.commit('changeActiveMenu', 5)
  }
})
